/**
 * Setup Service — unauthenticated fetch calls for the First-Run Setup Wizard.
 * No auth header required; these endpoints are only active before setup is complete.
 */

const getApiBase = () => {
  const hostname = window.location.hostname;
  const isProduction =
    hostname.includes('pages.dev') ||
    hostname.includes('cdbooking.com') ||
    hostname.includes('comdes.');
  return isProduction ? '/api' : (import.meta.env.VITE_API_URL || '/api');
};

/**
 * Check whether initial setup has already been completed.
 * @returns {{ success: boolean, setupComplete: boolean, userCount: number }}
 */
export async function getSetupStatus() {
  const res = await fetch(`${getApiBase()}/setup/status`);
  if (!res.ok) throw new Error('Failed to fetch setup status');
  return res.json();
}

/**
 * Does this system still need the first-run wizard? Only when setup is not
 * marked complete AND nobody exists yet — the same test POST /setup/complete
 * applies before it will deploy. A live system answers `setupComplete` with no
 * count, or a non-zero count; both mean "no".
 * @param {{ setupComplete?: boolean, userCount?: number }} status
 */
export function setupNeeded(status) {
  return !status?.setupComplete && status?.userCount === 0;
}

/**
 * Test a database / email provider connection during the wizard.
 * @param {string} provider  - e.g. 'cloudflare_d1', 'supabase'
 * @param {object} config    - provider-specific config fields
 * @returns {{ success: boolean, connected: boolean, message: string, latencyMs?: number }}
 */
export async function testConnection(provider, config) {
  const res = await fetch(`${getApiBase()}/setup/test-connection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, config }),
  });
  return res.json();
}

/**
 * Submit the completed wizard payload to finalise setup and create the master admin account.
 * @param {object} payload - full wizard state serialised for the API
 * @returns {{ success: boolean, token: string, user: object }}
 */
export async function completeSetup(payload) {
  const res = await fetch(`${getApiBase()}/setup/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Setup failed');
  }
  return res.json();
}
